import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiBook, FiChevronDown, FiChevronUp, FiSearch, FiCalendar } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  background: 
    radial-gradient(circle at 20% 50%, rgba(110, 168, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(45, 108, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(58, 143, 255, 0.08) 0%, transparent 50%),
    linear-gradient(135deg, #0a0f2e 0%, #1a2451 50%, #0e1633 100%);
  position: relative;
  overflow: hidden;
  color: #fff;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  background-image: 
    linear-gradient(45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(-45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #6ea8ff 75%),
    linear-gradient(-45deg, transparent 75%, #6ea8ff 75%);
  background-size: 60px 60px;
  background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
  pointer-events: none;
`;

const PageContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  animation: ${fadeIn} 0.6s ease-out;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #6ea8ff 0%, #ffffff 50%, #3a8fff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #b6d0ff;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  opacity: 0.9;
`;

const FiltersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 3rem;
  padding: 2rem;
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
  border: 1px solid rgba(110, 168, 255, 0.2);
  border-radius: 24px;
  backdrop-filter: blur(20px);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.6) 0%, rgba(20, 30, 60, 0.8) 100%);
  border: 1px solid rgba(110, 168, 255, 0.3);
  border-radius: 12px;
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:focus {
    outline: none;
    border-color: #6ea8ff;
    box-shadow: 0 0 0 3px rgba(110, 168, 255, 0.1);
  }

  &::placeholder {
    color: #b6d0ff;
    opacity: 0.7;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #b6d0ff;
  opacity: 0.7;
  width: 1.25rem;
  height: 1.25rem;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.6) 0%, rgba(20, 30, 60, 0.8) 100%);
  border: 1px solid rgba(110, 168, 255, 0.3);
  border-radius: 12px;
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:focus {
    outline: none;
    border-color: #6ea8ff;
    box-shadow: 0 0 0 3px rgba(110, 168, 255, 0.1);
  }

  option {
    background: #1e293b;
    color: #fff;
  }
`;

const ClearButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(145deg, rgba(110, 168, 255, 0.3) 0%, rgba(58, 143, 255, 0.4) 100%);
  border: 1px solid rgba(110, 168, 255, 0.3);
  border-radius: 12px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  backdrop-filter: blur(20px);

  &:hover {
    background: linear-gradient(145deg, rgba(110, 168, 255, 0.4) 0%, rgba(58, 143, 255, 0.5) 100%);
    transform: translateY(-2px) scale(1.02);
    border-color: rgba(110, 168, 255, 0.5);
    box-shadow: 0 8px 24px rgba(110, 168, 255, 0.3);
  }
`;

const ResultsInfo = styled.div`
  margin-bottom: 2rem;
  padding: 1rem;
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.6) 0%, rgba(20, 30, 60, 0.8) 100%);
  border: 1px solid rgba(110, 168, 255, 0.1);
  border-radius: 16px;
  color: #b6d0ff;
  font-size: 0.9rem;
  backdrop-filter: blur(20px);
  opacity: 0.9;
`;

const VersionCard = styled.div`
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
  border: 1px solid rgba(110, 168, 255, 0.2);
  border-radius: 24px;
  margin-bottom: 2rem;
  overflow: hidden;
  backdrop-filter: blur(20px);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(110, 168, 255, 0.4);
    transform: translateY(-2px);
    box-shadow: 
      0 30px 60px rgba(0, 0, 0, 0.4),
      0 0 40px rgba(110, 168, 255, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }
`;

const VersionHeader = styled.button`
  width: 100%;
  padding: 2rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  color: inherit;

  &:hover {
    background: rgba(45, 108, 255, 0.05);
  }
`;

const VersionHeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }
`;

const VersionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-align: left;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const VersionIcon = styled.span`
  font-size: 2rem;
`;

const VersionDetails = styled.div``;

const VersionNumber = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const VersionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const VersionBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.type) {
      case 'major': return 'background: #7c3aed; color: #fff;';
      case 'minor': return 'background: #2563eb; color: #fff;';
      case 'patch': return 'background: #059669; color: #fff;';
      case 'beta': return 'background: #ea580c; color: #fff;';
      default: return 'background: #6b7280; color: #fff;';
    }
  }}
`;

const VersionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #e2e8f0;
  margin: 0.5rem 0 0 0;
  font-weight: 500;
`;

const VersionDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #b6d0ff;
  opacity: 0.8;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const VersionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #b6d0ff;
  opacity: 0.8;

  @media (max-width: 768px) {
    align-self: stretch;
    justify-content: space-between;
  }
`;

const ChangeCount = styled.span`
  font-size: 0.9rem;
`;

const ExpandIcon = styled.div`
  transition: transform 0.3s ease;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const VersionContent = styled.div`
  padding: 0 2rem 2rem 2rem;
  border-top: 1px solid rgba(45, 108, 255, 0.1);
`;

const ChangeSection = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  
  ${props => {
    switch (props.type) {
      case 'features': return 'color: #10b981;';
      case 'improvements': return 'color: #3b82f6;';
      case 'fixes': return 'color: #ef4444;';
      default: return 'color: #94a3b8;';
    }
  }}
`;

const ChangeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ChangeItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  line-height: 1.6;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ChangeBullet = styled.span`
  ${props => {
    switch (props.type) {
      case 'features': return 'color: #10b981;';
      case 'improvements': return 'color: #3b82f6;';
      case 'fixes': return 'color: #ef4444;';
      default: return 'color: #94a3b8;';
    }
  }}
  font-weight: bold;
  margin-top: 0.1rem;
`;

const ChangeText = styled.span`
  color: #e2e8f0;
  font-size: 0.95rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #94a3b8;
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #e2e8f0;
`;

const EmptyDescription = styled.p`
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const Changelog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedVersions, setExpandedVersions] = useState(new Set(['2.0.4']));
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Real changelog data from the ChangelogModal
  const updateHistory = [
    {
      version: '2.2.2',
      title: 'Profile Deletion Fixes',
      releaseDate: '2025-01-24',
      type: 'patch',
      features: [],
      fixes: [
        'Fixed Profile Deletion with Schedules: Resolved foreign key constraint errors when deleting profiles that have associated schedules - deletion now works correctly for all profiles',
        'Enhanced Deletion Process: Added proper deletion order to handle all database relationships (schedules, schedule_runs, session_monitoring, session_actions, sessions, notifications)',
        'Smart Schedule Warning: Profile deletion now detects and warns users about schedules that will be deleted along with the profile, showing detailed count of active/inactive schedules',
        'Improved Bulk Deletion: Bulk profile deletion now properly handles profiles with schedules and shows schedule information in the warning'
      ],
      improvements: [
        'Better user experience with clear warnings about schedule deletion',
        'More reliable profile deletion across all scenarios',
        'Enhanced database integrity and foreign key constraint handling'
      ]
    },
    {
      version: '2.2.0',
      title: 'Advanced Session Monitoring System',
      releaseDate: '2025-01-24',
      type: 'minor',
      features: [
        'Real-time Session Health Monitoring: Complete monitoring system that tracks session health with heartbeat signals, activity detection, and browser health checks',
        'Enhanced Monitoring Logs: Beautiful new tabbed interface for viewing monitoring events with search, filtering, copy/export functionality, and color-coded event types',
        'Auto-Recovery System: Intelligent recovery attempts when sessions hang or encounter issues, with force termination after 3 strikes',
        'Heartbeat every 30 seconds for connection monitoring',
        'Activity checks every 15 seconds to detect automation progress',
        'Browser health checks every 60 seconds for stability',
        'Automatic hang detection within 90 seconds',
        'Complete local database logging of all monitoring events',
        'Health score indicators with visual status updates'
      ],
      fixes: [
        'Fixed Schedule Form Input Issues: Resolved typing problems in schedule creation forms - all input fields now work correctly',
        'Fixed Session Monitoring Data: Last Activity and Heartbeat now show separate, accurate timestamps instead of identical times',
        'Enhanced Session Reliability: Better detection and handling of stuck or unresponsive warming sessions'
      ],
      improvements: [
        'Enhanced session reliability with comprehensive monitoring',
        'Better debugging capabilities with detailed monitoring logs',
        'Improved user experience with visual health indicators'
      ]
    },
    {
      version: '2.1.5',
      title: 'Bug Fixes & Schedule Improvements',
      releaseDate: '2025-01-24',
      type: 'patch',
      features: [],
      fixes: [
        'Fixed Dolphin Anty Profile Deletion: Profiles now delete correctly using the proper API',
        'Fixed Schedule Creation: Profile selection dropdown now appears when creating schedules',
        'Fixed Scheduled Sessions: Better service detection for automatic warming sessions',
        'Fixed Dolphin Anty Scheduling: Scheduled warming sessions now work properly with Dolphin Anty profiles'
      ],
      improvements: [
        'Enhanced schedule creation workflow',
        'Better multi-service support for scheduling',
        'Improved profile management across all browser services'
      ]
    },
    {
      version: '2.1.4',
      title: 'New Google Service & Session Fixes',
      releaseDate: '2025-01-24',
      type: 'minor',
      features: [
        'Enhanced Google Browsing Service: Complete overhaul of Google automation with smart search patterns, realistic user behavior, and improved cookie consent handling',
        'Advanced Cookie Management: Automatic detection and handling of cookie consent banners across Google and websites',
        'Amazon Link Control: Toggle option to enable/disable clicking Amazon links during Google browsing sessions for more granular control',
        'Smart Action Filtering: Intelligent action selection based on current page context and session state'
      ],
      fixes: [
        'Fixed Session Status Race Condition: Sessions now properly complete with "completed" status instead of being incorrectly marked as "failed" during planned shutdowns',
        'Improved Browser Disconnect Handling: Better handling of browser disconnections during session completion to prevent status overwrites',
        'Enhanced Session Cleanup: Proper cleanup of session flags and cancellation tokens to prevent interference between session end and browser shutdown'
      ],
      improvements: [
        'More natural Google browsing patterns',
        'Better session completion reliability',
        'Enhanced cookie consent handling across all sites'
      ]
    },
    {
      version: '2.1.3',
      title: 'Critical Session Management Fixes & Enhanced Debugging',
      releaseDate: '2025-01-24',
      type: 'patch',
      features: [
        'Automatic Error Screenshots: Logger service now automatically captures browser screenshots when errors occur, providing visual debugging context for troubleshooting session issues',
        'Smart Screenshot Management: Automatic cleanup of old screenshots (7+ days) to prevent disk space issues'
      ],
      fixes: [
        'Fixed False CAPTCHA Detection: Resolved overly aggressive CAPTCHA detection that was incorrectly flagging normal Amazon "Continue Shopping" buttons as CAPTCHAs',
        'Fixed Sessions Running Over Time: Sessions now strictly respect their allocated time limits with a 30-second safety buffer to prevent overruns',
        'Fixed Session Logging Issues: Resolved issue where session logging would stop working when sessions ran over time - all actions are now properly logged throughout the entire session',
        'Enhanced Time Management: Added comprehensive time checks before navigation, CAPTCHA handling, and each action to ensure sessions end on schedule',
        'Improved Error Recovery: Error recovery now only attempts when sufficient time remains, preventing additional time overruns'
      ],
      improvements: [
        'Reduced navigation timeouts from 60s to 45s for more predictable timing',
        'Added intelligent action limiting when sessions approach time limits (only quick actions allowed in final 30 seconds)',
        'Enhanced session cleanup to ensure proper state saving regardless of how sessions end',
        'Added detailed session timer logging with progress tracking and time warnings',
        'Improved CAPTCHA detection specificity to only trigger on actual security challenges',
        'Screenshots saved to organized directory structure with timestamped filenames for easy identification',
        'Integrated screenshot capture into Amazon service error handling for better debugging visibility'
      ]
    },
    {
      version: '2.1.2',
      title: 'Bug Fixes & Improvements',
      releaseDate: '2025-01-24',
      type: 'patch',
      features: [],
      fixes: [
        'Fixed Dolphin Anty browser starting issues - profiles now launch correctly without connection errors',
        'Fixed bulk warming setup not showing the progress modal - bulk operations now display proper progress tracking and status updates'
      ],
      improvements: [
        'Enhanced browser connection reliability',
        'Better progress tracking for bulk operations'
      ]
    },
    {
      version: '2.1.1',
      title: 'Bug Fixes & Improvements',
      releaseDate: '2025-01-24',
      type: 'patch',
      features: [],
      fixes: [
        'Fixed bulk schedule creation generating identical random times for all profiles - each profile now gets unique randomized schedules as intended',
        'Fixed Dolphin Anty browser starting issues - profiles now launch correctly without connection errors',
        'Fixed bulk warming setup not showing the progress modal - bulk operations now display proper progress tracking and status updates'
      ],
      improvements: [
        'Better randomization for bulk schedule creation',
        'Enhanced browser connection handling',
        'Improved bulk operation feedback'
      ]
    },
    {
      version: '2.1.0',
      title: 'Enhanced Schedule Creation & Cookie Improvements',
      releaseDate: '2025-01-24',
      type: 'minor',
      features: [
        'Improved Schedule Wizard: Brand new guided schedule creation experience with step-by-step setup for both single and bulk schedules',
        'Bulk Schedule Deviation: Added time deviation controls to bulk schedule creation for more natural timing variations',
        'Clear Orphaned Sessions button in the active sessions view'
      ],
      fixes: [
        'Fixed sessions occasionally getting stuck in "active" state after completion',
        'Resolved database sync issues that could cause session history inconsistencies',
        'Fixed memory leaks in browser connection handling for better long-term stability',
        'Improved error handling for network timeouts during profile operations',
        'Fixed rare crashes when switching between multiple browser services quickly'
      ],
      improvements: [
        'More intuitive schedule creation process with clear navigation between steps',
        'Better visual feedback during schedule setup and editing',
        'Enhanced reliability for cookie acceptance across different websites',
        'Improved session management and cleanup for more stable operation'
      ]
    },
    {
      version: '2.0.8',
      title: 'Session Timing & Cookie Fixes',
      releaseDate: '2025-01-24',
      type: 'patch',
      features: [],
      fixes: [
        'Fixed sessions running over their allocated time limits',
        'Sessions now use random durations between min/max instead of fixed minimums',
        'Added time checks during browsing actions to prevent overruns',
        'Improved the cookie accepting during initial cookies, some were failing'
      ],
      improvements: [
        'Better session timing management',
        'More natural session duration variations',
        'Enhanced cookie acceptance reliability'
      ]
    },
    {
      version: '2.0.7',
      title: 'Browser Fixes & Amazon Improvements',
      releaseDate: '2025-01-24',
      type: 'patch',
      features: [],
      fixes: [
        'Fixed AdsPower User Agent Bug: Resolved browser version mismatch issues in AdsPower profiles',
        'Improved Profile Consistency: Better browser version alignment and fingerprint stability'
      ],
      improvements: [
        'Enhanced Wishlist Features: Better product discovery and wishlist management',
        'Improved Flow: More natural browsing patterns and smoother navigation',
        'Better Error Handling: More reliable Amazon automation with improved recovery'
      ]
    },
    {
      version: '2.0.5',
      title: 'Bulk Operations & UI Improvements',
      releaseDate: '2025-01-24',
      type: 'minor',
      features: [
        'Japan Region Support: Added full support for Japan (🇯🇵) - create profiles for Amazon Japan with proper localization, timezone, and Japanese websites',
        'Bulk Schedule Creation: Create schedules for multiple profiles at once with progress tracking',
        'Bulk Profile Deletion: Select and delete multiple profiles with confirmation and progress feedback',
        'Improved Profile View: Cleaner layout and better visual design for last session details for easier profile management'
      ],
      fixes: [
        'Fixed active sessions not properly removing from local database when sessions end',
        'Improved session cleanup and local database synchronization',
        'Enhanced backend app stability for session management'
      ],
      improvements: [
        'Better bulk operation workflows',
        'Enhanced profile management interface',
        'Improved session tracking and cleanup'
      ]
    },
    {
      version: '2.0.3',
      title: 'Bulk Profile Creation System',
      releaseDate: '2025-01-23',
      type: 'minor',
      features: [
        'Bulk Profile Creation: Create multiple profiles at once with customizable settings and intelligent proxy distribution',
        'Smart Proxy Assignment: Automatic proxy distribution with duplicate prevention when enough proxies are available',
        'OS Selection Controls: Choose single operating system or random selection from Windows, Mac, and Linux options',
        'Real-time Creation Progress: Live progress tracking with pause/resume and cancellation support',
        'Detailed Creation Queue: Preview all profiles with proxy assignments and OS selections before creation',
        'Configurable Creation Delays: Adjust delays between profile creation to avoid rate limiting'
      ],
      fixes: [
        'Fixed Reset Button Bug: Reset button no longer wipes completed sites progress - now preserves your work and continues from where you left off',
        'Fixed Enable Button Bug: Re-enabling initial cookies after disabling now preserves all completed sites instead of starting fresh',
        'Fixed Progress Loss: Both disable/enable and reset operations now properly maintain your completed sites data instead of restarting the initial cookies process',
        'Fixed browser service API authentication issues causing 403 errors during profile creation',
        'Improved error handling and user feedback for failed profile creation attempts',
        'Enhanced connection validation for all browser services before bulk operations'
      ],
      improvements: [
        'Enhanced profile creation workflow with better user guidance and validation',
        'Improved bulk operations performance with optimized API calls and error recovery',
        'Better visual feedback and status indicators throughout the creation process'
      ]
    },
    {
      version: '2.0.2',
      title: 'Major Amazon Rework, Initial Cookies System, Basket/Wishlist Management & Browser Reliability',
      releaseDate: '2025-01-16',
      type: 'minor',
      features: [
        'MAJOR Amazon Model Rework: Complete separation into modular functions for maximum flexibility and maintainability',
        'Dynamic Amazon Service Architecture: Fully redesigned to be more dynamic and extensible with separate handlers for each action type',
        'Advanced Basket & Wishlist Management: Complete overhaul with intelligent state tracking and automatic cleanup',
        'Profile Details Enhancement: Added comprehensive basket and wishlist details display in profile details page',
        'Initial Cookies System Rework: Complete redesign with selectable plans, timing controls, and minimum completion requirements',
        'Smart Initial Cookies Recovery: If initial cookies fail, system automatically continues from where it left off on next run',
        'Implemented custom ConfirmModal component with dark theme integration',
        'Added useConfirm React hook for streamlined confirmation dialog management',
        'Enhanced confirmation dialogs with backdrop blur, framer-motion animations, and responsive design',
        'Introduced modal type system: warning, danger, info with color-coded styling'
      ],
      fixes: [
        'Initial Cookies Progress Calculation: Fixed basic plan showing 8 minutes instead of 4 minutes in progress bars',
        'Fixed wishlist creation failures and XPath compatibility issues in Amazon service',
        'Resolved basket state synchronization problems across warming sessions',
        'Resolved critical issue where profile deletion bypassed confirmation prompts',
        'Fixed window.confirm() calls in AppContext deleteAdsPowerProfile method',
        'Eliminated native system dialogs that broke dark theme consistency',
        'Corrected ProfilesView delete button event handlers to use custom modals',
        'Fixed ActiveSessionsView session clearing to use themed confirmation system',
        'MAJOR FIX: Increased browser protocol timeouts from 45s to 90s to prevent session failures',
        'Fixed "Runtime.callFunctionOn timed out" errors that were causing browsers to disconnect',
        'Improved browser connection stability for all services (AdsPower, Dolphin Anty, Octo Browser)',
        'Better handling of slow browser operations and complex page interactions',
        'Fixed ESLint no-inner-declarations error in ScheduleManager by moving generateRunTimesArray function to component scope',
        'Fixed ESLint no-case-declarations errors in ProfilesView by wrapping variable declarations in case blocks with proper scoping',
        'Fixed ESLint no-useless-escape error in ActiveSessionsView by removing unnecessary escape characters in template strings',
        'Resolved build compilation failures caused by ESLint treating warnings as errors in production builds'
      ],
      improvements: [
        'Dramatically Enhanced Amazon Service: Modular architecture with separated functions for search, browse, basket, wishlist, and product interactions',
        'Intelligent Session State Management: Advanced tracking of user context, login status, page state, and interaction history',
        'Smart Action Selection: Dynamic action filtering based on current page context and realistic user behavior patterns',
        'Robust Basket Operations: Enhanced add-to-cart functionality with multiple fallback selectors and intelligent error handling',
        'Complete Wishlist System: Full wishlist creation, management, and item addition with automatic retry mechanisms',
        'Performance Optimizations: Significantly faster execution with optimized waiting times and intelligent page detection',
        'Enhanced Profile Insights: Detailed basket and wishlist information display in profile details for better session tracking',
        'Initial Cookies Flexibility: Customizable plans (fast, basic, balanced, thorough) with precise timing and completion tracking',
        'Fault-Tolerant Initial Cookies: Automatic resume functionality prevents starting over if initial cookies are interrupted',
        'Enhanced background processing and memory management for better stability and performance',
        'Enhanced UX with smooth modal transitions and backdrop effects',
        'Improved accessibility with proper focus management and keyboard navigation',
        'Optimized modal rendering performance with conditional mounting',
        'Better mobile responsiveness for confirmation dialogs on smaller screens',
        'Unified confirmation dialog styling across entire application',
        'Improved code quality and maintainability by adhering to ESLint best practices',
        'Enhanced build system stability by resolving all blocking ESLint errors',
        'Better code organization with proper function declaration placement and variable scoping',
        'Modular service architecture for better maintainability and feature development',
        'Significantly more reliable warming sessions - reduced failures by ~80%',
        'Better session completion rates for all warming plans',
        'Improved performance on slower systems and complex browser profiles'
      ]
    },
    {
      version: '2.0.1',
      title: 'Critical Browser Integration Bug Fixes & Service Connection Stability',
      releaseDate: '2025-01-16',
      type: 'patch',
      features: [],
      fixes: [
        'Fixed Dolphin Anty browser service initialization failure during warming session startup',
        'Resolved incorrect update notification display when user version matches latest release',
        'Improved Local API connection handling and retry logic for Dolphin Anty integration',
        'Fixed browser process spawning errors in multi-service environments'
      ],
      improvements: [
        'Enhanced error handling and debugging capabilities for browser service connections',
        'Implemented smarter update version detection and notification logic',
        'Optimized browser service health checks and connection validation',
        'Improved logging and diagnostic output for troubleshooting browser issues'
      ]
    },
    {
      version: '2.0.0',
      title: 'Complete UI Overhaul & Power User Features',
      releaseDate: '2025-06-16',
      type: 'major',
      features: [
        'Complete Dashboard Redesign - 16+ customizable, draggable and resizable cards with modern design',
        'Bulk Warming System - Launch multiple profiles with staggered timing and individual/shared settings',
        'Advanced Session Management - Delete individual, bulk, or all session history with enhanced tracking',
        'Multi-Browser Support - Octo Browser and Dolphin Anty now fully supported in production (moved from beta)',
        'Referrer Management System - Complete commission tracking and analytics for referrer programs (Contact for info)',
        'Warming Goals UI - New intuitive warming goals interface in profile details tab for tracking and managing objectives'
      ],
      fixes: [
        'Fixed duplicate notification issue when sessions end or encounter errors',
        'Enhanced session cleanup and browser process management for better stability',
        'Improved error handling and connection resilience throughout the application'
      ],
      improvements: [
        'Modern UI/UX - Enhanced visual design, animations, loading states, and responsive design',
        'Performance Optimizations - Faster loading, better API integration, and improved database operations',
        'Enhanced Analytics - Flexible time periods, real-time data updates, and advanced reporting'
      ]
    },
    {
      version: '1.2.0-beta.3',
      title: 'Octo Browser Support (Beta)',
      releaseDate: null,
      type: 'beta',
      features: [
        'Full Octo Browser Integration alongside AdsPower and Dolphin Anty',
        'Dynamic Dashboard with moveable and editable cards',
        'Enhanced Multi-Service Support with automatic detection',
        'Octo Browser Local API Support for browser launching',
        'Cross-Platform Profile Management for all three services'
      ],
      fixes: [
        'Fixed Active Sessions displaying "Unknown" for sites',
        'Fixed sites data storage and retrieval in session database',
        'Improved session data consistency across view components',
        'Enhanced error handling for browser service detection'
      ],
      improvements: [
        'Enhanced browser controller for multiple services',
        'Better error handling and diagnostic messages',
        'Comprehensive API integration with CRUD operations',
        'New dynamic dashboard with customizable layout'
      ]
    },
    {
      version: '1.2.0-beta.1',
      title: 'Dolphin Anty Support (Beta)',
      releaseDate: null,
      type: 'beta',
      features: [
        'Full Dolphin Anty Integration alongside AdsPower profiles',
        'Automatic Service Detection for profile management',
        'Dolphin Anty Local API Support for browser automation',
        'Cross-Platform Profile Management from single interface'
      ],
      fixes: [
        'Fixed notification bubble display issues',
        'Fixed active sessions not ending correctly when started through schedule',
        'Fixed timeCheckInterval reference errors during session management',
        'Resolved hardcoded service references in log messages'
      ],
      improvements: [
        'Enhanced browser controller for multiple antidetect services',
        'Improved error handling with service-specific diagnostics',
        'Better debugging and logging for browser connections',
        'Enhanced session management consistency'
      ]
    },
    {
      version: '1.1.9',
      title: 'Profile Tracking & UI Improvements',
      releaseDate: null,
      type: 'minor',
      features: [
        'Profile view displays last run stats and daily run amounts',
        'Real-time notification updates regardless of session start method',
        'Updated app logo for refreshed look'
      ],
      fixes: [
        'Fixed notification bubble not showing on scheduled sessions',
        'Fixed expiry page information display and edge cases',
        'Fixed edge cases and runtime errors in session display'
      ],
      improvements: [
        'Improved profile layout for better readability',
        'Enhanced consistency of badges and action buttons',
        'Better notification bubble positioning and updates',
        'Various UI/UX tweaks for smoother experience'
      ]
    },
    {
      version: '1.1.8',
      title: 'Guides & Session Management',
      releaseDate: null,
      type: 'minor',
      features: [
        'Guides now display correctly (Amazon 1 week and 2 month methods)',
        'Real-time session count badges in sidebar',
        'Profile "WARMING" badges during active sessions',
        'On-demand session actions loading for better performance'
      ],
      fixes: [
        'Fixed guides display to show only relevant content',
        'Fixed site display and session data handling in history',
        'Fixed issues with session display and runtime errors'
      ],
      improvements: [
        'Accurate scrollable site activity lists in profile details',
        'Better session management with disabled buttons during warming',
        'Improved "Clear Sessions" to "End All Sessions" with better UX',
        'Consistent badge and button sizing throughout app'
      ]
    },
    {
      version: '1.1.5',
      title: 'Bug Fixes & Improvements',
      releaseDate: null,
      type: 'patch',
      features: [
        'Initial cookies process now supports cancellation',
        'Clear message about initial cookies timing and data usage'
      ],
      fixes: [
        'Fixed "Clear All" notifications button functionality',
        'Fixed session history duration display format',
        'Fixed "Last Activity" time display consistency',
        'Fixed profile editing in Detail Screen',
        'Fixed "Start Warming" functionality in History view',
        'Fixed Dashboard View Details functionality'
      ],
      improvements: [
        'Improved error handling for initial cookies with timeouts/retries',
        'Enhanced browser disconnection handling and cleanup',
        'Better session cancellation and browser process management',
        'Enhanced error reporting in Active Sessions view',
        'Improved session duration calculation and progress tracking'
      ]
    }
  ];

  const toggleVersion = (version) => {
    setExpandedVersions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(version)) {
        newSet.delete(version);
      } else {
        newSet.add(version);
      }
      return newSet;
    });
  };

  const getVersionTypeIcon = (type) => {
    switch (type) {
      case 'major': return '🚀';
      case 'minor': return '✨';
      case 'patch': return '🔧';
      case 'beta': return '🧪';
      default: return '📦';
    }
  };

  // Enhanced filter logic
  const filteredHistory = updateHistory.filter(update => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const matchesSearch = normalizedSearchTerm === '' || 
      update.version.toLowerCase().includes(normalizedSearchTerm) ||
      update.title.toLowerCase().includes(normalizedSearchTerm) ||
      (update.features && update.features.some(f => f.toLowerCase().includes(normalizedSearchTerm))) ||
      (update.fixes && update.fixes.some(f => f.toLowerCase().includes(normalizedSearchTerm))) ||
      (update.improvements && update.improvements.some(f => f.toLowerCase().includes(normalizedSearchTerm)));

    const matchesCategory = selectedCategory === 'all' || 
      (selectedCategory === 'features' && update.features && update.features.length > 0) ||
      (selectedCategory === 'fixes' && update.fixes && update.fixes.length > 0) ||
      (selectedCategory === 'improvements' && update.improvements && update.improvements.length > 0);

    return matchesSearch && matchesCategory;
  });

  const clearSearch = () => {
    setSearchTerm('');
    setSelectedCategory('all');
  };

  const getTotalChanges = (update) => {
    return (update.features?.length || 0) + (update.fixes?.length || 0) + (update.improvements?.length || 0);
  };

  return (
    <Container>
      <BackgroundPattern />
      <Header />
      <PageContent>
        <PageHeader>
          <Title>Version History</Title>
          <Subtitle>
            Track the evolution of DeservedWarmer with detailed release notes, 
            new features, improvements, and bug fixes for every version.
          </Subtitle>
        </PageHeader>

        <FiltersSection>
          <SearchContainer>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder="Search versions, features, fixes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>

          <FilterContainer>
            <FilterSelect
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Changes</option>
              <option value="features">New Features</option>
              <option value="fixes">Bug Fixes</option>
              <option value="improvements">Improvements</option>
            </FilterSelect>

            {(searchTerm || selectedCategory !== 'all') && (
              <ClearButton onClick={clearSearch}>
                Clear Filters
              </ClearButton>
            )}
          </FilterContainer>
        </FiltersSection>

        <ResultsInfo>
          Showing {filteredHistory.length} of {updateHistory.length} versions
          {searchTerm && ` matching "${searchTerm}"`}
          {selectedCategory !== 'all' && ` with ${selectedCategory}`}
        </ResultsInfo>

        {filteredHistory.length === 0 ? (
          <EmptyState>
            <EmptyTitle>No versions found</EmptyTitle>
            <EmptyDescription>
              Try adjusting your search terms or filters to find what you're looking for.
            </EmptyDescription>
            <ClearButton onClick={clearSearch}>
              Clear Filters
            </ClearButton>
          </EmptyState>
        ) : (
          filteredHistory.map((update) => (
            <VersionCard key={update.version}>
              <VersionHeader onClick={() => toggleVersion(update.version)}>
                <VersionHeaderContent>
                  <VersionInfo>
                    <VersionIcon>{getVersionTypeIcon(update.type)}</VersionIcon>
                    <VersionDetails>
                      <VersionNumber>
                        <VersionTitle>v{update.version}</VersionTitle>
                        <VersionBadge type={update.type}>{update.type}</VersionBadge>
                      </VersionNumber>
                      <VersionSubtitle>{update.title}</VersionSubtitle>
                      {update.releaseDate && (
                        <VersionDate>
                          <FiCalendar />
                          {new Date(update.releaseDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </VersionDate>
                      )}
                    </VersionDetails>
                  </VersionInfo>
                  <VersionMeta>
                    <ChangeCount>{getTotalChanges(update)} changes</ChangeCount>
                    <ExpandIcon expanded={expandedVersions.has(update.version)}>
                      <FiChevronDown size={20} />
                    </ExpandIcon>
                  </VersionMeta>
                </VersionHeaderContent>
              </VersionHeader>

              {expandedVersions.has(update.version) && (
                <VersionContent>
                  {/* Features */}
                  {update.features && update.features.length > 0 && (selectedCategory === 'all' || selectedCategory === 'features') && (
                    <ChangeSection>
                      <SectionTitle type="features">
                        <span>✨</span>
                        New Features ({update.features.length})
                      </SectionTitle>
                      <ChangeList>
                        {update.features.map((feature, idx) => (
                          <ChangeItem key={idx}>
                            <ChangeBullet type="features">•</ChangeBullet>
                            <ChangeText>{feature}</ChangeText>
                          </ChangeItem>
                        ))}
                      </ChangeList>
                    </ChangeSection>
                  )}

                  {/* Improvements */}
                  {update.improvements && update.improvements.length > 0 && (selectedCategory === 'all' || selectedCategory === 'improvements') && (
                    <ChangeSection>
                      <SectionTitle type="improvements">
                        <span>🚀</span>
                        Improvements ({update.improvements.length})
                      </SectionTitle>
                      <ChangeList>
                        {update.improvements.map((improvement, idx) => (
                          <ChangeItem key={idx}>
                            <ChangeBullet type="improvements">•</ChangeBullet>
                            <ChangeText>{improvement}</ChangeText>
                          </ChangeItem>
                        ))}
                      </ChangeList>
                    </ChangeSection>
                  )}

                  {/* Bug Fixes */}
                  {update.fixes && update.fixes.length > 0 && (selectedCategory === 'all' || selectedCategory === 'fixes') && (
                    <ChangeSection>
                      <SectionTitle type="fixes">
                        <span>🐛</span>
                        Bug Fixes ({update.fixes.length})
                      </SectionTitle>
                      <ChangeList>
                        {update.fixes.map((fix, idx) => (
                          <ChangeItem key={idx}>
                            <ChangeBullet type="fixes">•</ChangeBullet>
                            <ChangeText>{fix}</ChangeText>
                          </ChangeItem>
                        ))}
                      </ChangeList>
                    </ChangeSection>
                  )}
                </VersionContent>
              )}
            </VersionCard>
          ))
        )}
      </PageContent>
      <Footer />
    </Container>
  );
};

export default Changelog; 